import { Component } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import { aiqLogger } from 'services/aiqLogger';
import ProtocolPanelFrame from 'features/stepByStepProtocol/components/ProtocolPanelFrame/ProtocolPanelFrame';
import ProtocolUnavailableCard from 'features/stepByStepProtocol/components/ProtocolUnavailableCard/ProtocolUnavailableCard';

// A failed chunk import stays failed until a reload (React.lazy keeps it), so every later open fails the
// same way: each distinct failure is logged once per page load.
const loggedFailures = new Set();

// Keeps a panel that cannot load its chunk (e.g. the first open after a deploy) or fails to render from
// taking down the AIQ page: the panel shows the Unavailable card with what helps, a reload.
class ProtocolPanelErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, info) {
        const { transactionId } = this.props;
        const failure = String(error?.message || error);
        if (loggedFailures.has(failure)) return;
        loggedFailures.add(failure);
        try {
            aiqLogger.log(
                {
                    message: `[ProtocolPanelErrorBoundary] ${error?.message || error}`,
                    transactionId,
                    componentStack: info?.componentStack,
                    stack: error?.stack,
                },
                'react-error-boundary',
                'error'
            );
        } catch {
            // never let logging break the boundary itself
        }
        Sentry.captureException(error, {
            tags: { boundary: 'protocol-panel', transactionId: String(transactionId ?? '') },
            extra: { transactionId, componentStack: info?.componentStack },
        });
    }

    render() {
        const { onClose, children } = this.props;
        if (!this.state.hasError) return children;
        return (
            <ProtocolPanelFrame onClose={onClose}>
                <ProtocolUnavailableCard note="Reload the page to try again." />
            </ProtocolPanelFrame>
        );
    }
}

ProtocolPanelErrorBoundary.propTypes = {
    transactionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    onClose: PropTypes.func.isRequired,
    children: PropTypes.node,
};

export default ProtocolPanelErrorBoundary;
